import { Button, Icons } from '@commenda/kakashi';
import type { MouseEvent } from 'react';

import { useCommendaAiHost } from './CommendaAiHostProvider';
import { MarkdownLinkHelper } from './markdown/MarkdownLinkHelper';

import { MissingRegistrationState } from '../indirect-tax/calculator/MissingRegistrationState';
import { TaxBreakdown } from '../indirect-tax/calculator/TaxBreakdown';
import type { TaxSummaryView } from '../indirect-tax/calculator/types';

/** What chat's calculate_tax call priced: the address the engine calculated for and the Tax calculator's
 * own breakdown of the result. Mirrors {@link AccountManagerCard}'s geometry so the cards sit flush in a
 * message. */
interface TaxCalculationCardProps {
  entityName: string | null;
  addressLabel: string;
  isStreetGiven: boolean;
  isNoStateSalesTax: boolean;
  summary: TaxSummaryView;
  calculatorLink: string | null;
}

export function TaxCalculationCard({
  entityName,
  addressLabel,
  isStreetGiven,
  isNoStateSalesTax,
  summary,
  calculatorLink,
}: Readonly<TaxCalculationCardProps>) {
  const { navigate, openRecord } = useCommendaAiHost();

  // Routed like chat's markdown links, so the console switches into the entity's client first.
  const onCalculatorLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!calculatorLink) return;
    const target = MarkdownLinkHelper.toInternalTarget(
      calculatorLink,
      window.location.origin
    );
    if (
      !target ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    )
      return;
    event.preventDefault();
    if (openRecord) openRecord(target);
    else navigate(target.path);
  };

  return (
    <div className="flex w-full flex-col gap-lg rounded-lg border-[0.5px] border-border-primary bg-bg-primary p-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between gap-md">
        <span className="flex items-center gap-sm">
          <Icons.Calculator className="size-4 shrink-0 text-fg-tertiary" />
          <span className="txt-small-medium text-text-primary">
            Commenda tax calculator
          </span>
        </span>
        {calculatorLink ? (
          <Button asChild variant="secondary" size="sm">
            <a
              href={calculatorLink}
              onClick={onCalculatorLinkClick}
              rel="noopener noreferrer"
            >
              Open in Tax calculator
            </a>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-xs">
        {entityName ? (
          <span className="txt-small-regular text-text-primary">
            Entity: {entityName}
          </span>
        ) : null}
        <span className="txt-small-regular text-text-primary">
          Calculated for: {addressLabel}
        </span>
        {isStreetGiven ? null : (
          <span className="txt-extra-small-regular text-text-tertiary">
            No street given — rate based on ZIP
          </span>
        )}
        {isNoStateSalesTax ? (
          <span className="txt-extra-small-regular text-text-tertiary">
            No state sales tax
          </span>
        ) : null}
      </div>

      {summary.unregisteredJurisdictionName ? (
        <MissingRegistrationState
          jurisdictionName={summary.unregisteredJurisdictionName}
        />
      ) : (
        <TaxBreakdown summary={summary} />
      )}
    </div>
  );
}
